/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  filterSystemRolesEnumDictionary,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleTemplateRestService,
  ScheduleType,
  UserRestService,
  UserSystemRole,
} from '@gh/builder-shared-data';
import { EntityInputDialogConfig } from '@gh/common-internal-shared';

import {
  DictionaryService,
  getRouteParam,
  InitDialogViewService,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';

import {
  ScheduleTaskUserAutocomplete,
  ScheduleTaskUserAutocompleteFilter,
  ScheduleTemplateTaskDetails,
  scheduleTemplateTaskDetailsFromDto,
  scheduleTemplateTaskDetailsToDto,
  ScheduleTemplateTaskNoteListItem,
} from './scheduling-schedule-template-task.model';

@Injectable()
export class SchedulingScheduleTemplateTaskBusinessService
  implements InitViewService, InitDialogViewService<EntityInputDialogConfig<ScheduleTemplateTaskNoteListItem>> {

  constructor(private gridLayout: GridLayoutService,
              private dictionaryService: DictionaryService,
              private userRestService: UserRestService,
              private restService: ScheduleTemplateRestService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      systemRoles: this.dictionaryService.getByEnum(UserSystemRole),
      changeDateAfterCompleteTypes: this.dictionaryService.getByEnum(ScheduleTaskChangeDateAfterCompleteType),
      scheduleTaskTypes: this.dictionaryService.getByEnum(ScheduleTaskType),
      scheduleTemplateTaskTodoEventType: this.dictionaryService.getByEnum(ScheduleTaskTodoEventType),
      todoGridLayout: this.gridLayout.get('scheduleTemplateTaskTodos'),
      noteGridLayout: this.gridLayout.get('scheduleTemplateTaskNotes'),
      taskDetails: this.get(getRouteParam(route, 'templateId'), getRouteParam(route, 'taskId')),
    };
  }

  initDialogView(route: DialogRouteSnapshot<EntityInputDialogConfig<ScheduleTemplateTaskNoteListItem>>): any {
    return route.input;
  }

  initTodoDialogView(): any {
    return {
      systemRoles: this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesEnumDictionary(
        ['Franchisee', 'Estimator', 'Supervisor', 'JobAdmin', 'Accountant', 'Salesperson']))),
      salesSystemRoles: this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesEnumDictionary(
        ['Franchisee', 'Accountant', 'Salesperson', 'JobAdmin']))),
      todoEventTypes: this.dictionaryService.getByEnum(ScheduleTaskTodoEventType),
    };
  }

  get(templateId: ID, taskId: ID): Observable<ScheduleTemplateTaskDetails> {
    return this.restService.getTask(templateId, taskId).pipe(map$(scheduleTemplateTaskDetailsFromDto));
  }

  update(templateId: ID,
         taskId: ID,
         scheduleTemplateTask: ScheduleTemplateTaskDetails,
        type: ScheduleType): Observable<ScheduleTemplateTaskDetails> {
    const task = scheduleTemplateTaskDetailsToDto(scheduleTemplateTask);
    return this.restService
      .updateTask(templateId, taskId, type === ScheduleType.Construction ? {...task, isInSupervisorSchedule: true} : task).pipe(
        map$(scheduleTemplateTaskDetailsFromDto));
  }

  getAutocompleteUser(query: FilteredDataQuery<ScheduleTaskUserAutocompleteFilter>): Observable<ScheduleTaskUserAutocomplete[]> {
    return this.userRestService.findAllAsDataSource({
      name: query.filter && query.filter.name || '',
      showInactive: false,
      autocomplete: true,
      systemRoles: query.filter && query.filter.systemRoles || void 0,
    }).select(query).pipe(map$(getSlicedData));
  }
}
